import { useCallback } from "react";
import PlaceholderCard from "../../components/PlaceholderCard";
import StatTile from "../../components/StatTile";
import VolumeChartCard from "../../components/VolumeChartCard";
import { NoteCard, CardGrid, ErrorBanner, LoadingNote, Section } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import {
  fetchCustomerStats,
  fetchCustomerTexting,
  fetchCustomerVolume,
} from "../../dashboardApi";
import {
  CustomerStatsResponse,
  CustomerTextingResponse,
  CustomerVolumeResponse,
  MessagingSource,
} from "../../types";
import { colors, type } from "../../styles";
import { formatCents, formatNumber } from "../../format";

/**
 * Per-customer DELIVERABILITY was deliberately removed and stays removed —
 * Jonathan doesn't trust Markit's Firestore log as much as Twilio's own data,
 * and doesn't consider the per-customer slice of it important. The
 * platform-wide view (which he does want) lives on the Home tab's Channel
 * section, off the nightly rollup.
 *
 * Per-customer VOLUME is the opposite case on both counts, which is why it is
 * charted here: it comes from Twilio's own Usage Records at that creator's
 * sub-account, and a history of who is sending how much is exactly what he
 * asked for. The day rows behind the chart are the `byDay` map the nightly
 * rollup was already writing inside each month document — no new storage, just
 * a read of data that was being recorded all along, plus a one-time backfill of
 * the years before the job started running.
 *
 * Mass texts sent is a different thing entirely — Markit's own campaign count,
 * which Twilio has no concept of — so it stays a live Firestore count.
 */
const UNMEASURABLE: Record<
  Exclude<MessagingSource, "subaccount" | "dedicatedNumber">,
  { label: string; reason: string }
> = {
  sharedMainAccount: {
    label: "Not measurable",
    reason:
      "This creator has no Markit number of their own — free tier or not yet approved, so they send from Markit's shared number. Twilio bills that traffic in one pool and offers no way to attribute it per creator.",
  },
  unavailable: {
    label: "Not measurable",
    reason:
      "This creator's Markit number could not be resolved to a single owner, so crediting them with any of its traffic would be inventing a split.",
  },
};

/**
 * How the two MEASURED sources are described on the card.
 *
 * Both `spendCents` are now the SAME measurement — Twilio's real `totalprice`
 * for the account the traffic was billed to, carrier fees, number rental and
 * Conversations included — which is the whole point of the Sep 2026 rework. The
 * notes no longer warn the reader off comparing them.
 *
 * What the `dedicatedNumber` note still says is that the figure is allocated
 * rather than stated: that creator shares a Twilio account, and their share of
 * its bill is split by message segments because Twilio publishes neither carrier
 * fees per message nor Conversations per number. That is a real caveat and it
 * stays visible — it is just a much smaller one than "this is half the number".
 */
const MEASURED: Record<
  "subaccount" | "dedicatedNumber",
  { volumeNote: string; spendNote: string; chartSubtitle: string }
> = {
  subaccount: {
    volumeNote: "Twilio sub-account",
    spendNote: "Twilio's total for the sub-account",
    chartSubtitle: "Twilio Usage Records at this creator's sub-account",
  },
  dedicatedNumber: {
    volumeNote: "this creator's own Markit number",
    spendNote:
      "Their share of a shared Twilio account's total, split by message segments",
    chartSubtitle: "Twilio messages sent from this creator's own Markit number",
  },
};

/** A muted dash plus the reason, rather than a 0 that would read as "sent none". */
const UnmeasurableTile = ({
  label,
  reason,
  updatedAt,
}: {
  label: string;
  reason: string;
  updatedAt?: string;
}) => (
  <StatTile
    label={label}
    value={<span style={{ color: colors.inkFaint }}>—</span>}
    secondary={
      <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>{reason}</span>
    }
    title={reason}
    freshness={{ kind: "cached", updatedAt }}
  />
);

const TextingSection = ({ uid }: { uid: string }) => {
  const textingFetcher = useCallback(() => fetchCustomerTexting(uid), [uid]);
  const texting = useDashboardData<CustomerTextingResponse>(textingFetcher, [uid]);

  const statsFetcher = useCallback(() => fetchCustomerStats(uid), [uid]);
  const stats = useDashboardData<CustomerStatsResponse>(statsFetcher, [uid]);

  // Fetched separately from the totals above, same reason every other split on
  // this page exists: they are different reads, and one failing should not
  // blank the other.
  const volumeFetcher = useCallback(() => fetchCustomerVolume(uid), [uid]);
  const volume = useDashboardData<CustomerVolumeResponse>(volumeFetcher, [uid]);

  const rollup = stats.data?.stats;
  const syncedAt = stats.data?.sync.lastRunFinishedAt ?? undefined;
  const source = rollup?.messagingSource;
  const measured =
    source === "subaccount" || source === "dedicatedNumber"
      ? MEASURED[source]
      : undefined;
  const unmeasurable =
    source && !measured ? UNMEASURABLE[source as keyof typeof UNMEASURABLE] : undefined;

  return (
    <Section title="Texting">
      {texting.error && <ErrorBanner message={texting.error} />}
      {stats.error && <ErrorBanner message={stats.error} />}
      {texting.loading && <LoadingNote />}

      <CardGrid min={220}>
        {texting.data &&
          (texting.data.massTextsSent !== null ? (
            <StatTile
              label="Mass texts sent"
              value={formatNumber(texting.data.massTextsSent)}
              secondary="Lifetime · excludes automated trigger texts"
              freshness={{ kind: "live" }}
            />
          ) : (
            <PlaceholderCard
              title="Mass texts sent"
              source={texting.data.massTextsError ?? "Count unavailable."}
              shape="none"
            />
          ))}

        {unmeasurable ? (
          <UnmeasurableTile
            label="Messages sent"
            reason={unmeasurable.reason}
            updatedAt={syncedAt}
          />
        ) : (
          <StatTile
            label="Messages sent"
            value={rollup ? formatNumber(rollup.lifetime.messagesSent) : "—"}
            secondary={
              rollup
                ? `Last 30d ${formatNumber(rollup.trailing30d.messagesSent)}${
                    measured ? ` · ${measured.volumeNote}` : ""
                  }`
                : "Not yet recorded by the nightly rollup"
            }
            freshness={{ kind: "cached", updatedAt: syncedAt }}
          />
        )}

        {unmeasurable ? (
          <UnmeasurableTile
            label="Twilio spend"
            reason={unmeasurable.reason}
            updatedAt={syncedAt}
          />
        ) : (
          <StatTile
            label="Twilio spend"
            value={rollup ? formatCents(rollup.lifetime.spendCents) : "—"}
            secondary={
              rollup
                ? `Last 30d ${formatCents(rollup.trailing30d.spendCents)}${
                    measured ? ` · ${measured.spendNote}` : ""
                  }`
                : "Not yet recorded by the nightly rollup"
            }
            freshness={{ kind: "cached", updatedAt: syncedAt }}
          />
        )}
      </CardGrid>

      <div style={{ marginTop: 16 }}>
        {unmeasurable ? (
          // No chart at all, rather than an empty frame. An empty axis reads as
          // "this creator sent nothing", which is precisely the wrong reading —
          // their traffic is real, it just cannot be separated from the pool
          // Twilio bills it in.
          <NoteCard title="No volume history for this creator">
            {unmeasurable.reason} There is no per-day series to draw, and drawing
            an empty chart would read as &ldquo;sent nothing&rdquo; rather than
            &ldquo;cannot be measured&rdquo;. Platform-wide volume on the Home tab
            still includes this creator&rsquo;s traffic &mdash; it comes from one
            query at Markit&rsquo;s main Twilio account, which rolls up everything.
          </NoteCard>
        ) : (
          <VolumeChartCard
            title="Message volume"
            subtitle={
              measured?.chartSubtitle ?? "Twilio message volume for this creator"
            }
            days={volume.data?.days ?? []}
            error={volume.error}
            loading={volume.loading}
            dimmed={volume.refreshing}
            emptyLabel="No messages recorded for this creator yet."
            freshness={{
              kind: "cached",
              updatedAt: volume.data?.sync.lastRunFinishedAt ?? syncedAt,
            }}
          />
        )}
      </div>
    </Section>
  );
};

export default TextingSection;
